"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkSharing = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const web_blizzard_reporting_1 = require("@snapchat/web-blizzard-reporting");
const web_core_ui_1 = require("@snapchat/web-core-ui");
const web_sc_cookies_1 = require("@snapchat/web-sc-cookies");
const clsx_1 = __importDefault(require("clsx"));
const react_1 = require("react");
const device_detection_1 = require("../../device-detection");
const shareDestinations_1 = require("../../shareDestinations");
const sharingUtils_1 = require("../../sharingUtils");
const ShareBar_1 = require("../ShareBar");
const LinkSharing_module_css_1 = __importDefault(require("./LinkSharing.module.css"));
const event_1 = require("@snapchat/ts-protos/event");
const react_intl_1 = require("react-intl");
const web_env_1 = require("@snapchat/web-env");
const copyText = (text) => {
    const navigatorObj = (0, web_env_1.getNavigator)();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (navigatorObj?.clipboard) {
        void navigatorObj.clipboard.writeText(text);
    }
    else {
        console.error("Clipboard is undefined");
    }
};
const copyLinkClassName = (size) => {
    if (size === ShareBar_1.ShareBarSize.SMALL) {
        return LinkSharing_module_css_1.default.copyLinkSmall;
    }
    if (size === ShareBar_1.ShareBarSize.MEDIUM) {
        return LinkSharing_module_css_1.default.copyLinkMedium;
    }
    return LinkSharing_module_css_1.default.copyLinkLarge;
};
function LinkInput({ shareLink }) {
    return ((0, jsx_runtime_1.jsx)("div", { className: LinkSharing_module_css_1.default.linkContainer, children: (0, jsx_runtime_1.jsx)("input", { type: "text", readOnly: true, value: shareLink, className: LinkSharing_module_css_1.default.link, id: "shareLink", "data-testid": "linkSharingInputBox" }) }));
}
function CopyLinkButton({ size, onClick }) {
    return ((0, jsx_runtime_1.jsx)("button", { type: "button", className: copyLinkClassName(size), onClick: onClick, "data-testid": "linkSharingCopyButton", children: (0, jsx_runtime_1.jsx)(react_intl_1.FormattedMessage, { id: "zFaVQO", defaultMessage: "Copy Link", description: "Copy Link text" }) }));
}
function CopyLinkIcon({ onClick }) {
    return ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: onClick, className: (0, clsx_1.default)([LinkSharing_module_css_1.default.icon, LinkSharing_module_css_1.default.copyLinkIcon]), children: (0, jsx_runtime_1.jsx)(web_core_ui_1.LinkIcon, { size: "MEDIUM" }) }));
}
const copiedLinkIcon = ((0, jsx_runtime_1.jsx)("div", { className: (0, clsx_1.default)([LinkSharing_module_css_1.default.icon, LinkSharing_module_css_1.default.copiedLinkIcon]), children: (0, jsx_runtime_1.jsx)(web_core_ui_1.CheckIcon, { size: "MEDIUM" }) }));
/**
 *
 */
function LinkSharing({ shareLink, pageName, pageType, tooltipMessage, shareDestinations = shareDestinations_1.defaultShareBarShareDestinations, size, incrementCounter, }) {
    const [isMessageVisible, setMessageVisible] = (0, react_1.useState)(false);
    const [copied, setCopied] = (0, react_1.useState)(false);
    const webClientId = (0, web_sc_cookies_1.useGetWebClientId)() ?? "";
    const timerRef = (0, react_1.useRef)();
    const copyTimerRef = (0, react_1.useRef)();
    const handleTooltip = (0, react_1.useCallback)(() => {
        setMessageVisible(true); // show tooltip
        timerRef.current = (0, web_env_1.getWindow)()?.setTimeout(() => {
            setMessageVisible(false); // remove/hide tooltip
        }, 3000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMessageVisible]);
    const linkSharingWrapperClass = size === ShareBar_1.ShareBarSize.SMALL ? LinkSharing_module_css_1.default.wrapperSmall : LinkSharing_module_css_1.default.wrapperLarge;
    const copyAndEmitMetrics = (shareDestinations, shareLink, pageType, pageName) => {
        // TODO: When this component can also be used on mobile, set source device type correctly
        const preparedUrl = (0, sharingUtils_1.prepareUrlForSharing)(shareLink, device_detection_1.DeviceType.DESKTOP);
        copyText(preparedUrl);
        const shareSheetDestinations = shareDestinations.map((shareDestination) => shareDestination.shareDestination.toString());
        (0, web_blizzard_reporting_1.reportWebPageShare)({
            sessionTimeSec: 0,
            shareSource: event_1.WebShareSource.WEB_SHARE_BAR,
            webDestinationsAvailable: shareSheetDestinations.join(","),
            webDestinationsSeen: shareSheetDestinations.join(","),
            destinationSelected: event_1.WebShareDestination.COPY_LINK,
            shareStatus: event_1.WebShareStatus.WEB_SHARE_STATUS_SUCCESS,
            deepLinkUrl: preparedUrl,
            webPageUrl: shareLink,
            pageType: pageType,
            webClientId,
        });
        void incrementCounter(`${pageName}.Desktop.Share.Success.CopyLink`);
    };
    function handleClickWithTooltip() {
        copyAndEmitMetrics(shareDestinations, shareLink, pageType, pageName);
        handleTooltip();
    }
    const handleClick = (0, react_1.useCallback)(() => {
        copyAndEmitMetrics(shareDestinations, shareLink, pageType, pageName);
        setCopied(true);
        copyTimerRef.current = (0, web_env_1.getWindow)()?.setTimeout(() => {
            setCopied(false);
        }, 2000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [copied]);
    const buttonEl = size === ShareBar_1.ShareBarSize.SMALL ? ((0, jsx_runtime_1.jsx)(CopyLinkIcon, { onClick: handleClick })) : ((0, jsx_runtime_1.jsx)(CopyLinkButton, { size: size, onClick: handleClickWithTooltip }));
    (0, react_1.useEffect)(() => () => {
        clearTimeout(timerRef.current);
        clearTimeout(copyTimerRef.current);
    }, []);
    return ((0, jsx_runtime_1.jsxs)("div", { className: linkSharingWrapperClass, children: [size === ShareBar_1.ShareBarSize.LARGE && (0, jsx_runtime_1.jsx)(LinkInput, { shareLink: shareLink }), (0, jsx_runtime_1.jsxs)("div", { className: LinkSharing_module_css_1.default.buttonWrapper, children: [isMessageVisible && ((0, jsx_runtime_1.jsx)("div", { className: LinkSharing_module_css_1.default.tooltipContainer, children: (0, jsx_runtime_1.jsx)(web_core_ui_1.Tooltip, { message: tooltipMessage, position: web_core_ui_1.Position.TOP }) })), copied ? copiedLinkIcon : buttonEl] })] }));
}
exports.LinkSharing = LinkSharing;
//# sourceMappingURL=LinkSharing.js.map